/**
 * Fixed column widths and cell alignment for admin data tables (KIOSK-335.2).
 * Keeps headers and values aligned and prevents layout shift when sorting/refetching.
 */
import React from 'react';
import { cn } from '../utils';

export type ColumnAlign = 'left' | 'right';

export interface AdminTableColumnDef {
  /** CSS width, e.g. `12%` or `8rem` */
  width: string;
  align?: ColumnAlign;
  /** Use `tabular-nums` for money, dates, and counts */
  tabular?: boolean;
}

/** Shared 8-column withdrawal queue layout (withdrawal-requests + flagged-withdrawals). */
export const WITHDRAWAL_QUEUE_COLUMNS: AdminTableColumnDef[] = [
  { width: '14%' },
  { width: '14%' },
  { width: '10%' },
  { width: '10%', align: 'right', tabular: true },
  { width: '10%' },
  { width: '12%' },
  { width: '12%', tabular: true },
  { width: '18%', align: 'right' },
];

/** Manual game deposits — always 9 columns; "Time left" shows em dash off pending tab. */
export const MANUAL_GAME_DEPOSIT_COLUMNS: AdminTableColumnDef[] = [
  { width: '12%' },
  { width: '14%' },
  { width: '10%' },
  { width: '10%', align: 'right', tabular: true },
  { width: '10%' },
  { width: '12%', tabular: true },
  { width: '10%' },
  { width: '22%', align: 'right' },
];

export const PLAYER_WALLET_TX_COLUMNS: AdminTableColumnDef[] = [
  { width: '14%', tabular: true },
  { width: '10%' },
  { width: '28%' },
  { width: '12%', align: 'right', tabular: true },
  { width: '22%', align: 'right', tabular: true },
  { width: '14%' },
];

export const PLAYERS_LIST_COLUMNS: AdminTableColumnDef[] = [
  { width: '14%' },
  { width: '17%' },
  { width: '15%' },
  { width: '9%' },
  { width: '9%' },
  { width: '12%', tabular: true },
  { width: '12%', tabular: true },
  { width: '12%', align: 'right' },
];

/** Payment transactions list (`/transactions`) — 10 columns. */
export const PAYMENT_TRANSACTION_COLUMNS: AdminTableColumnDef[] = [
  { width: '5%', tabular: true },
  { width: '12%' },
  { width: '7%' },
  { width: '8%', align: 'right', tabular: true },
  { width: '9%', align: 'right', tabular: true },
  { width: '8%' },
  { width: '6%' },
  { width: '28%' },
  { width: '12%', tabular: true },
  { width: '5%', align: 'right' },
];

export const ADMIN_TABLE_FIXED_CLASS = 'table-fixed w-full';

/** Matches `@kioskgaming/ui` TableHead / TableCell rhythm on raw `<table>` markup. */
export const ADMIN_RAW_CELL = 'px-4 py-3 align-middle';

/**
 * Clip overflowing text inside `table-fixed` cells.
 * `max-w-0` is required: without it, the cell grows with content and paints over the next column.
 */
export const ADMIN_CLIP_CELL = 'max-w-0 overflow-hidden';

export function adminCellClass(
  column?: AdminTableColumnDef,
  extra?: string,
): string {
  return cn(
    column?.align === 'right' && 'text-right',
    column?.tabular && 'tabular-nums',
    extra,
  );
}

export function AdminTableColgroup({ columns }: { columns: AdminTableColumnDef[] }) {
  return (
    <colgroup>
      {columns.map((col, index) => (
        <col key={`${col.width}-${index}`} style={{ width: col.width }} />
      ))}
    </colgroup>
  );
}

/** Shorthand indices for withdrawal queue tables */
export const WQ = {
  tx: 0,
  party: 1,
  type: 2,
  amount: 3,
  status: 4,
  review: 5,
  created: 6,
  actions: 7,
} as const;

export const MD = {
  tx: 0,
  user: 1,
  game: 2,
  amount: 3,
  status: 4,
  created: 5,
  timeLeft: 6,
  actions: 7,
} as const;

export const WT = {
  date: 0,
  type: 1,
  description: 2,
  amount: 3,
  balance: 4,
  reference: 5,
} as const;

export const PL = {
  username: 0,
  emailPhone: 1,
  agentShop: 2,
  status: 3,
  bonus: 4,
  createdAt: 5,
  lastActivity: 6,
  actions: 7,
} as const;

export const PT = {
  id: 0,
  buyer: 1,
  status: 2,
  usdAmount: 3,
  creditAmount: 4,
  gateway: 5,
  method: 6,
  txnId: 7,
  created: 8,
  actions: 9,
} as const;
